import { useEffect, useState } from "react";
import axios from "axios";
import { Download, LogOut, Loader2 } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

const TOKEN_KEY = "otti_admin_token";
const authHeader = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem(TOKEN_KEY)}` } });

const LoginForm = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, data?.token ?? "");
      onDone();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field = "mt-2 w-full rounded-xl border border-[#D3C7B7] bg-white px-4 py-3 text-base outline-none focus:border-[#1C2B4A]";
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="mx-auto mt-16 max-w-sm rounded-3xl border border-[#E4DDD3] bg-[#FAF4ED] p-8">
      <h1 className="font-display text-3xl">Waitlist admin</h1>
      <label className="mt-6 block text-sm font-semibold">Email
        <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
      </label>
      <label className="mt-4 block text-sm font-semibold">Password
        <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} />
      </label>
      {error && <p data-testid="admin-login-error" className="mt-3 text-sm text-[#A23E1B]">{error}</p>}
      <button data-testid="admin-login-button" disabled={busy} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#16203A] py-3 font-semibold text-white">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
      </button>
    </form>
  );
};

const toCsv = (rows) =>
  ["email,joined_at", ...rows.map((r) => `"${String(r.email).replace(/"/g, '""')}",${r.created_at}`)].join("\n");

const WaitlistTable = ({ rows, onLogout }) => {
  const download = () => {
    const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: "otti-waitlist.csv" });
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="mx-auto mt-12 max-w-4xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl">Waitlist</h1>
          <p data-testid="admin-subscriber-count" className="mt-2 text-[#464E62]">{rows.length} {rows.length === 1 ? "person has" : "people have"} joined</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="admin-download-csv-button" onClick={download} className="inline-flex items-center gap-2 rounded-full bg-[#1C2B4A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#121C33]">
            <Download className="h-4 w-4" /> Download CSV
          </button>
          <button data-testid="admin-logout-button" onClick={onLogout} className="inline-flex items-center gap-2 rounded-full border border-[#D3C7B7] px-4 py-2.5 text-sm font-semibold">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>
      <div className="mt-8 overflow-x-auto rounded-2xl border border-[#E4DDD3] bg-white">
        <table data-testid="admin-subscribers-table" className="w-full text-left text-sm">
          <thead className="bg-[#EFEBE3] text-[#464E62]">
            <tr><th className="px-5 py-3 font-semibold">Email</th><th className="px-5 py-3 font-semibold">Joined</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={2} className="px-5 py-8 text-center text-[#5A6176]">No signups yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EFEBE3]">
                <td className="break-all px-5 py-3">{r.email}</td>
                <td className="whitespace-nowrap px-5 py-3 text-[#464E62]">{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function Admin() {
  const [rows, setRows] = useState(null);
  const [authed, setAuthed] = useState(Boolean(sessionStorage.getItem(TOKEN_KEY)));

  useEffect(() => {
    if (!authed) return undefined;
    axios.get(`${API}/admin/waitlist`, authHeader())
      .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
      .catch(() => { sessionStorage.removeItem(TOKEN_KEY); setAuthed(false); });
    return undefined;
  }, [authed]);

  const logout = () => { sessionStorage.removeItem(TOKEN_KEY); setRows(null); setAuthed(false); };

  return (
    <div className="min-h-screen bg-[#FAF7F2] px-5 py-8 text-[#16203A] sm:px-8" data-testid="admin-page">
      <div className="mx-auto max-w-4xl"><Logo /></div>
      {!authed && <LoginForm onDone={() => setAuthed(true)} />}
      {authed && rows === null && <p className="mt-16 text-center text-[#464E62]">Loading…</p>}
      {authed && rows !== null && <WaitlistTable rows={rows} onLogout={logout} />}
    </div>
  );
}
